import React from 'react';
import { CheckCheck, Calculator, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const ageInDays = (value) => {
  if (!value) return null;
  return Math.round((Date.now() - new Date(value).getTime()) / 86400000);
};

/** Pick which costing becomes the quoting basis for an inquiry item. */
export const ItemCostingChoiceDialog = ({ item, inquiryId, open, onOpenChange, onApprove, busy }) => {
  const navigate = useNavigate();
  const options = item?.costing_options || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="item-costing-choice-dialog">
        <DialogHeader>
          <DialogTitle>Costings for {item?.internal_part_no || item?.description}</DialogTitle>
          <DialogDescription>
            These costings were auto-linked from the part. Open one to check it, then approve the one to quote from.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[50vh] space-y-2 overflow-y-auto">
          {options.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="costing-options-empty">
              This part has no costing yet.
            </p>
          )}
          {options.map((costing) => {
            const approved = costing.id === item?.costing_id;
            const age = ageInDays(costing.created_at);
            return (
              <div key={costing.id} className="flex flex-wrap items-center justify-between gap-3 border p-3"
                data-testid={`costing-option-${costing.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-semibold">{costing.costing_number}</span>
                    <Badge variant="outline">{costing.status}</Badge>
                    {costing.is_active
                      ? <Badge className="bg-emerald-100 text-emerald-800" data-testid={`costing-valid-${costing.id}`}>current version</Badge>
                      : <Badge className="bg-slate-100 text-slate-700" data-testid={`costing-superseded-${costing.id}`}>older version</Badge>}
                    {costing.link_source === 'part_master' && (
                      <Badge variant="outline" className="text-xs">auto-linked from part</Badge>
                    )}
                    {approved && <Badge className="bg-blue-100 text-blue-800">in use</Badge>}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    ₹{Number(costing.selling_price_per_piece || 0).toFixed(4)}/pc
                    {costing.selling_price_per_kg ? ` · ₹${Number(costing.selling_price_per_kg).toFixed(2)}/kg` : ''}
                    {costing.quantity ? ` · qty ${Number(costing.quantity).toLocaleString('en-IN')}` : ''}
                    {costing.created_at ? ` · ${new Date(costing.created_at).toLocaleDateString('en-IN')}` : ''}
                    {age !== null ? ` · ${age} day(s) old` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline"
                    onClick={() => window.open(`/costings/${costing.id}`, '_blank', 'noopener')}
                    data-testid={`view-costing-option-${costing.id}`}>
                    <ExternalLink className="mr-2 h-4 w-4" /> View details
                  </Button>
                  <Button size="sm" disabled={busy || approved} onClick={() => onApprove(costing.id)}
                    data-testid={`approve-costing-option-${costing.id}`}>
                    <CheckCheck className="mr-2 h-4 w-4" /> {approved ? 'In use' : 'Use this'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="sm:justify-between">
          <p className="text-xs text-muted-foreground">
            None of these fit? Cost the part fresh in the costing calculator.
          </p>
          <Button variant="outline" disabled={!item?.part_master_id}
            onClick={() => navigate(`/costings/new?part=${item.part_master_id}&inquiry=${inquiryId}&product=${item.id}`)}
            data-testid="create-new-costing-from-dialog">
            <Calculator className="mr-2 h-4 w-4" /> Create a new costing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ItemCostingChoiceDialog;
